/**
 * What the hero's panel says when the control plane cannot be reached.
 *
 * The front page is static and knows it: the headline, the pitch and the three
 * facts are in this bundle and ask the API for nothing. The one thing on it
 * that does need the API is signing in — so an unreachable control plane costs
 * the panel, and must not cost the page.
 *
 * Not the sign-in form with an error over it. Whatever stopped `/api/session`
 * will stop `/api/login` too, so a handle field here would take a handle and
 * fail with it. The panel says the site cannot be signed into and offers the
 * one thing that ever helps, which is asking again.
 *
 * No prose of its own, deliberately: the message is the one api.ts already
 * writes for an unreachable API, and the button says what it does. The card
 * has no heading for the same reason the error screen's card has none — the
 * page it sits on already has its h1. Nothing here is a placeholder waiting on
 * a human, because nothing here is copy.
 *
 * `card` is the class the sign-in form shares its base look with, so the panel
 * this stands in for and this one are the same object in the same place; only
 * the hero's own translucency rule has to name it as well.
 */

import { el } from "./dom";

/**
 * The panel, and the button that re-runs whatever the caller passes.
 *
 * `retry` is the router's own start(), so pressing this reads the session
 * again and puts up whichever screen it should have been all along — the
 * signed-in home included, if the outage was over by then.
 */
export function outagePanel(message: string, retry: () => void): HTMLElement {
  const again = el("button", { type: "button", textContent: "Try again" });
  again.addEventListener("click", () => {
    again.disabled = true;
    again.textContent = "Retrying…";
    retry();
  });

  return el("section", { className: "card hero-outage" }, [
    el("p", { className: "error", role: "alert", textContent: message }),
    el("div", { className: "row" }, [again]),
  ]);
}
